import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiFolder, FiPlus, FiShare2, FiMoreVertical, FiSettings, FiTrash2 } from 'react-icons/fi';
import AddBoardModal from '../components/AddBoardModal';
import BoardSettingsModal from '../components/BoardSettingsModal';
import CreateInviteModal from '../components/CreateInviteModal';
import './Boards.css';

const Boards = ({ token, user }) => {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const actionMenuRef = useRef(null);

  useEffect(() => {
    fetchBoards();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchBoards = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBoards(data.boards || []);
      }
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBoard = (newBoard) => {
    fetchBoards();
  };

  const handleCreateInvite = (invite) => {
    // Invite created successfully
  };

  const handleBoardUpdate = () => {
    fetchBoards();
  };

  const handleBoardDelete = () => {
    fetchBoards();
  };

  const openBoardSettings = (board, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedBoard(board);
    setShowSettingsModal(true);
    setActionMenuOpen(null);
  };

  const openShareModal = (board, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedBoard(board);
    setShowInviteModal(true);
    setActionMenuOpen(null);
  };

  const isAdminOrPM = user?.role === 'Admin' || user?.role === 'PM';

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="boards-page">
      <div className="boards-header">
        <h1 className="boards-title">Boards</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          {isAdminOrPM && (
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowInviteModal(true)}
              data-testid="share-client-button"
            >
              <FiShare2 size={18} />
              <span>Share with Client</span>
            </button>
          )}
          <button 
            className="btn btn-primary" 
            onClick={() => setShowAddModal(true)}
            data-testid="create-board-button"
          >
            <FiPlus size={18} />
            <span>New Board</span>
          </button>
        </div>
      </div>

      <AddBoardModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddBoard}
        token={token}
      />
      
      <BoardSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        board={selectedBoard}
        token={token}
        onUpdate={handleBoardUpdate}
        onDelete={handleBoardDelete}
      />
      
      <CreateInviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onCreate={handleCreateInvite}
        token={token}
      />

      <div className="boards-grid">
        {boards.map((board) => {
          const initials = (board.name || '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0])
            .join('')
            .toUpperCase() || '?';
          const color = board.color || '#0F0F10';
          return (
            <div key={board.id} className="board-card-wrapper">
              <Link
                to={`/boards/${board.id}`}
                className="board-card board-card-v2"
                data-testid="board-card"
                style={{ borderLeft: `4px solid ${color}` }}
              >
                <div className="board-card-top">
                  <div className="board-card-circle"
                    style={{ background: color, color: '#FFFFFF' }}
                    aria-hidden="true">
                    {initials}
                  </div>
                  <div className="board-card-top-meta">
                    <h3 className="board-card-name">{board.name}</h3>
                    {user?.role === 'Admin' && (
                      <button
                        type="button"
                        className="board-card-edit-link"
                        onClick={(e) => openBoardSettings(board, e)}
                        data-testid={`board-edit-${board.id}`}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                <div className="board-card-client">{board.client}</div>
                <div className="board-card-type">{board.projectType}</div>
                {board.metrics && (
                  <div className="board-card-metrics">
                    <div className="board-metric">
                      <span className="board-metric-label">Progress</span>
                      <span className="board-metric-value">{board.metrics.progress}%</span>
                    </div>
                    <div className="board-metric">
                      <span className="board-metric-label">Tasks</span>
                      <span className="board-metric-value">{board.metrics.totalTasks}</span>
                    </div>
                    <div className="board-metric">
                      <span className="board-metric-label">Groups</span>
                      <span className="board-metric-value">{board.metrics.groupCount}</span>
                    </div>
                  </div>
                )}
                {board.metrics && (
                  <div className="board-card-stats">
                    <span className="board-stat" style={{ color: '#1DBF73' }}>
                      {board.metrics.completedTasks} Done
                    </span>
                    <span className="board-stat" style={{ color: '#E24B5B' }}>
                      {board.metrics.overdueTasks} Overdue
                    </span>
                    <span className="board-stat" style={{ color: '#E24B5B' }}>
                      {board.metrics.blockedTasks} Blocked
                    </span>
                  </div>
                )}
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Boards;